import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { featuresQuery, isHardwareEnabledInMap } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page";
import { DataTable, Td, Th } from "@/components/ui/table";
import { DEVICE_TYPE_LABELS } from "@/features/devices/device-labels";
import { useApiClient } from "@/lib/api-context";
import { getAppLocale } from "@/i18n";
import { findCatalogueEntry, type CatalogueEntry, type Specification } from "./catalogue";
import { localizeCatalogueEntry } from "./catalogue-locale";

/**
 * Everything known about one supported device, and how much of it is actually
 * known.
 *
 * The page is organised around that second half. A specification sheet that
 * mixed the handful of facts we can stand behind with the marketing copy and
 * the guesses would be worse than no page at all, because somebody would
 * quote it into a proposal (ADR-049).
 *
 * Hence four separate blocks rather than one list: what the label says, what
 * the manufacturer claims, what our own code does, and what nobody has
 * checked. Only the third is ours to vouch for.
 */
export function SupportedDeviceDetailPage() {
  const client = useApiClient();
  const features = useQuery(featuresQuery(client));
  const { id = "" } = useParams<{ id: string }>();
  const found = findCatalogueEntry(id);

  if (!found) {
    return <MissingHardware />;
  }

  // Only hide once flags have loaded. While pending, treat as enabled (same
  // default as the API) so a successful page does not flash the missing state.
  if (
    features.isSuccess &&
    !isHardwareEnabledInMap(features.data.enabledHardware, id)
  ) {
    return <MissingHardware />;
  }

  return <DeviceDetail entry={localizeCatalogueEntry(found, getAppLocale())} />;
}

function MissingHardware() {
  const { t } = useTranslation("pages");

  return (
    <>
      <PageHeader
        title={t("supportedMissingTitle")}
        description={t("supportedMissingDescription")}
      />
      <Link
        to="/supported-devices"
        className="text-sm text-brand-strong underline underline-offset-2"
      >
        {t("hardwareBack")}
      </Link>
    </>
  );
}

function DeviceDetail({ entry }: { entry: CatalogueEntry }) {
  const { t } = useTranslation("pages");
  const { user } = useAuth();
  const canRegister = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const measuresWhat = entry.deviceType
    ? DEVICE_TYPE_LABELS[entry.deviceType].toLowerCase()
    : null;

  return (
    <>
      <PageHeader
        title={`${entry.manufacturer} ${entry.model}`}
        description={
          measuresWhat ? t("hardwareMeasures", { what: measuresWhat }) : t("hardwareDocumentedOnly")
        }
        action={
          canRegister && !entry.pending ? (
            <Button to="/devices/new">{t("registerOneOfThese")}</Button>
          ) : null
        }
      />

      <p className="mb-4 text-sm">
        <Link to="/supported-devices" className="text-brand-strong underline underline-offset-2">
          {t("hardwareBack")}
        </Link>
      </p>

      <Card>
        <CardBody className="grid gap-6 p-5 md:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] md:items-start">
          <figure className="m-0">
            <img
              src={entry.imageSrc}
              alt={`${entry.manufacturer} ${entry.model}`}
              width={640}
              height={640}
              className="w-full rounded-lg bg-surface-muted object-contain p-4"
            />
            <figcaption className="mt-2 text-xs text-ink-muted">
              {t("hardwarePhotoCaption")}
            </figcaption>
          </figure>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              {entry.pending ? <Badge tone="neutral">{t("supportPending")}</Badge> : null}
              {entry.verifiedOnHardware ? (
                <Badge tone="ok">{t("testedYes")}</Badge>
              ) : (
                <Badge tone="warn">{t("testedNo")}</Badge>
              )}
            </div>

            <p className="mt-2 text-sm text-ink-muted">{entry.summary}</p>

            {/*
             * The pending notice goes first and the untested one is dropped
             * when it applies. "We cannot ingest this" already implies nobody
             * has tested it, and stacking two warnings would dilute the one
             * that changes what the reader can do.
             */}
            {entry.pending ? (
              <Alert tone="info" className="mt-3" announce={false}>
                <p>
                  <span className="font-medium">{t("hardwareCannotRegister")}</span>{" "}
                  {entry.pending.reason}
                </p>
                <p className="mt-2">
                  <span className="font-medium">{t("hardwareWhatItWouldTake")}</span>{" "}
                  {entry.pending.blockedOn}
                </p>
              </Alert>
            ) : entry.verifiedOnHardware ? null : (
              <Alert tone="warn" className="mt-3" announce={false}>
                {t("hardwareUntestedModel")}
              </Alert>
            )}

            <p className="mt-3 text-sm">
              <span className="text-ink-muted">{t("hardwareSoldBy", { reseller: entry.reseller })}</span>{" "}
              <a
                href={entry.listingUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium text-brand-strong underline underline-offset-2"
              >
                {entry.listingName}
              </a>
              <span className="text-ink-muted">{t("hardwarePart", { sku: entry.sku })}</span>
            </p>

            <SpecificationList title={t("hardwareOnLabel")} items={entry.confirmed} />
          </div>
        </CardBody>

        <div className="border-t border-border-subtle">
          <CardHeader
            title={t("hardwareManufacturerClaims")}
            description={t("hardwareClaimsHint", { reseller: entry.reseller })}
          />
          <CardBody>
            <ul className="flex flex-col gap-3">
              {entry.claimed.map((claim) => (
                <li key={claim.feature} className="text-sm">
                  <p className="font-medium">{claim.feature}</p>
                  <p className="mt-0.5 text-ink-muted">{claim.soWhat}</p>
                </li>
              ))}
            </ul>
          </CardBody>
        </div>

        <div className="border-t border-border-subtle">
          <CardHeader
            title={t("hardwarePlatformDoes")}
            description={t("hardwarePlatformDoesHint")}
          />
          <CardBody>
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {entry.platformBehaviour.map((item) => (
                <div key={item.label}>
                  <dt className="text-xs font-semibold tracking-wide text-ink-muted uppercase">
                    {item.label}
                  </dt>
                  <dd className="mt-0.5 text-sm">{item.value}</dd>
                </div>
              ))}
            </dl>
          </CardBody>
        </div>

        <div className="border-t border-border-subtle">
          <CardHeader
            title={t("hardwareOpenQuestions")}
            description={t("hardwareOpenQuestionsHint")}
          />
          <CardBody className="px-0 py-0">
            <DataTable caption={t("hardwareUnverifiedCaption", { model: entry.model })}>
              <thead>
                <tr>
                  <Th>{t("hardwareColTopic")}</Th>
                  <Th>{t("hardwareColWhy")}</Th>
                  <Th>{t("hardwareColWho")}</Th>
                </tr>
              </thead>
              <tbody>
                {entry.toVerify.map((question) => (
                  <tr key={question.topic}>
                    <Td className="font-medium">{question.topic}</Td>
                    <Td className="text-ink-muted">{question.why}</Td>
                    <Td className="text-ink-muted">{question.answeredBy}</Td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </CardBody>
        </div>
      </Card>
    </>
  );
}

function SpecificationList({ title, items }: { title: string; items: readonly Specification[] }) {
  return (
    <div className="mt-4">
      <h3 className="text-xs font-semibold tracking-wide text-ink-muted uppercase">{title}</h3>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {items.map((item) => (
          <div key={item.label} className="contents">
            <dt className="text-ink-muted">{item.label}</dt>
            <dd className="font-medium">
              {item.value}
              {item.source ? (
                <span className="ml-1.5 font-normal text-ink-muted">({item.source})</span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
